import { ControlSection, ToggleControlItem } from '@bloomfieldrobotics/canopy';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { useCallback, useEffect, useState } from 'react';
import { useGetBlocksForCollectionQuery } from '../../../../services/entities/impl/blfApi/endpoints/collections';
import { Block } from '../../../../services/entities/impl/blfApi/schemas/block';
import { Collection } from '../../../../services/entities/impl/blfApi/schemas/collection';
import LoadingSpinner from '../../../commonUI/LoadingSpinner';

type Props = {
  collectionId: Collection['id'];
  onBlockToggle: (
    hiddenBlockIds: Block['id'][],
    toggleState?: Map<Block['id'], boolean>
  ) => void;
};
export const CollectionBlocksControlSection = ({
  collectionId,
  onBlockToggle,
}: Props) => {
  const { currentData, isLoading: isLoadingBlocks } =
    useGetBlocksForCollectionQuery(collectionId ?? skipToken);
  const blocks = currentData?.items;

  // Multi-toggle state
  const [toggleState, setToggleState] = useState<Map<string, boolean>>(
    new Map()
  );

  const handleBlockItemToggle = useCallback(({ id }: Block) => {
    setToggleState((prev) => {
      const prevToggleItemState = prev.get(id);
      return new Map(prev).set(id, !prevToggleItemState);
    });
  }, []);

  /**
   * This is technically an anti-pattern/misuse of useEffect
   * https://react.dev/learn/you-might-not-need-an-effect
   *
   * Normally you should do all the logic that is within this useEffect
   * inside of the handleBlockItemToggle.
   *
   * However, because we are handling toggle state here we have to set state
   * based on the previous value of the state. And we cannot move the logic
   * within the function passed to setToggleState either, because that SetStateAction
   * should be "pure", and not trigger/depend on props.
   *
   * So, that is why we are utilizing this useEffect with only a dependency on toggleState
   */
  useEffect(() => {
    const hiddenBlockIds: Block['id'][] = Array.from(toggleState)
      .filter(([, isSelected]) => isSelected)
      .map(([blockId]) => blockId);

    onBlockToggle(hiddenBlockIds, toggleState);
  }, [toggleState]);

  return (
    <ControlSection title='Block'>
      {!isLoadingBlocks ? (
        blocks?.map((block) => (
          <ToggleControlItem
            key={block.id}
            label={block.name}
            active={!toggleState.get(block.id)}
            onChange={() => {
              handleBlockItemToggle(block);
            }}
          />
        ))
      ) : (
        <LoadingSpinner />
      )}
    </ControlSection>
  );
};
